"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { logger } from "@/src/lib/logging/logger";
import { Appointment, AppointmentMetadataFiltersParams } from "@/src/models/appointment";
import { getAppointmentById, getPaginatedAppointmentsMetadata, updateAppointmentById } from "@/src/requests/appointment";
import { isNotFound } from "@/src/requests/utils/fetch-json";
export const PAGINATED_APPOINTMENTS_CACHE_KEY = "paginated-appointments-metadata";
export const APPOINTMENTS_CACHE_KEY = ["appointments"] as const;
export function updateAppointmentsCache(queryClient: ReturnType<typeof useQueryClient>, incoming: Appointment[]) {
  queryClient.setQueryData(APPOINTMENTS_CACHE_KEY, (old: Appointment[] = []) => {
    const map = new Map(old.map(a => [a.id, a]));
    incoming.forEach(a => map.set(a.id, a));
    return Array.from(map.values());
  });
}
export function useAppointmentById(id) {
  const $ = _c(14);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== id) {
    t0 = [...APPOINTMENTS_CACHE_KEY, id];
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== id || $[3] !== queryClient) {
    t1 = async () => {
      const globalAppointments = queryClient.getQueryData(APPOINTMENTS_CACHE_KEY);
      const cachedAppointment = globalAppointments?.find(apt => apt.id === id || apt.displayId === id);
      if (cachedAppointment) {
        return cachedAppointment;
      }
      ;
      try {
        const appointment = await getAppointmentById(id);
        if (appointment) {
          queryClient.setQueryData([...APPOINTMENTS_CACHE_KEY, id], appointment);
          updateAppointmentsCache(queryClient, [appointment]);
        }
        return appointment;
      } catch (t2) {
        const err = t2;
        if (isNotFound(err as Error)) {
          return null;
        }
        logger.error("Error fetching appointment", {
          error: err
        });
        throw err;
      }
    };
    $[2] = id;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!id;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data: appointment_0,
    isFetching,
    isLoading,
    isError
  } = useQuery(t3);
  let t4;
  if ($[9] !== appointment_0 || $[10] !== isError || $[11] !== isFetching || $[12] !== isLoading) {
    t4 = {
      appointment: appointment_0,
      isFetching,
      isLoading,
      isError
    };
    $[9] = appointment_0;
    $[10] = isError;
    $[11] = isFetching;
    $[12] = isLoading;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function usePaginatedAppointmentsMetadata(params) {
  const $ = _c(18);
  let t0;
  if ($[0] !== params.createdBy || $[1] !== params.page || $[2] !== params.pageSize || $[3] !== params.sortBy || $[4] !== params.sortOrder) {
    t0 = [PAGINATED_APPOINTMENTS_CACHE_KEY, params.page, params.pageSize, params.sortBy, params.sortOrder, params.createdBy];
    $[0] = params.createdBy;
    $[1] = params.page;
    $[2] = params.pageSize;
    $[3] = params.sortBy;
    $[4] = params.sortOrder;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== params) {
    t1 = () => getPaginatedAppointmentsMetadata(params);
    $[6] = params;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 30000
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const {
    data,
    isLoading,
    isError
  } = useQuery(t2);
  let t3;
  if ($[11] !== data?.appointments) {
    t3 = data?.appointments ?? [];
    $[11] = data?.appointments;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = data?.totalCount;
  let t5;
  if ($[13] !== isError || $[14] !== isLoading || $[15] !== t3 || $[16] !== t4) {
    t5 = {
      appointments: t3,
      totalCount: t4,
      isLoading,
      isError
    };
    $[13] = isError;
    $[14] = isLoading;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export function useUpdateAppointmentMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: updated => {
        queryClient.setQueryData([...APPOINTMENTS_CACHE_KEY, updated.id], updated);
        queryClient.setQueryData([...APPOINTMENTS_CACHE_KEY, updated.displayId], updated);
        updateAppointmentsCache(queryClient, [updated]);
        queryClient.invalidateQueries({
          queryKey: [PAGINATED_APPOINTMENTS_CACHE_KEY]
        });
      },
      onError: _temp2
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp2(error) {
  logger.error("Error updating appointment", {
    error
  });
}
async function _temp(t0) {
  const {
    id,
    updates
  } = t0;
  return updateAppointmentById(id, updates);
}